import React, { useRef, useEffect } from 'react';
import { CalendarDayProps } from '../../types';
import { classNames } from '../../../../utils';
import { nullFn, compareDates, isEqualDate } from '../../utils';
import { useRangeFromStartDate, useRangeFromEndDate } from '../../hooks';
import { useCalendar } from './Calendar';
import CalendarRangeHighlight from './CalendarRangeHighlight';

export default function CalendarDay({
  date,
  firstOfMonth,
  minValue = undefined,
  maxValue = undefined,
  isFirstDayOfWeek = false,
  isLastDatOfWeek = false,
  isSelected = false,
  isWithinRange = false,
  onChange = nullFn,
}: CalendarDayProps): JSX.Element {
  const { focusedDate, currentRange } = useCalendar();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const day = date.getDate();
  const isAdjacentDate = date.getMonth() !== firstOfMonth.getMonth();
  const isDisabled =
    maxValue && minValue
      ? compareDates(date, maxValue) > 0 || compareDates(date, minValue) < 0
      : false;
  const tabIdx = isEqualDate(focusedDate, date) ? 0 : -1;
  const isRangeStartDate = useRangeFromStartDate(date, currentRange);
  const isRangeEndDate = useRangeFromEndDate(date, currentRange);
  const isToday = isEqualDate(date, new Date());

  useEffect(() => {
    if (buttonRef.current && tabIdx !== -1) {
      buttonRef.current.focus();
    }
  }, [tabIdx]);

  if (isAdjacentDate || isDisabled) {
    return (
      <td className='box-border w-9 rounded-full p-0 text-center font-medium text-gray-dark'>
        <div className='my-2 mx-auto h-9 leading-9'>
          <span className='my-1 mx-auto inline-block h-9 w-9 select-none bg-transparent text-center leading-9 text-gray-light'>
            {day}
          </span>
        </div>
      </td>
    );
  }

  return (
    <td className='box-border w-9 rounded-full p-0 text-center text-base font-medium text-gray-dark'>
      <CalendarRangeHighlight
        isVisible={isWithinRange && !(isRangeStartDate && isRangeEndDate)}
        isFirstInRange={isRangeStartDate}
        isLastInRange={isRangeEndDate}
        isFirstDayOfWeek={isFirstDayOfWeek}
        isLastDayOfWeek={isLastDatOfWeek}
      >
        <button
          ref={buttonRef}
          tabIndex={tabIdx}
          onClick={() => onChange(date)}
          className={classNames(
            'relative h-9 w-9 cursor-pointer rounded-lg leading-9 outline-none focus:outline-none',
            isToday &&
              'realtive after:absolute after:bottom-0 after:left-1/2 after:mb-0.5 after:block after:h-2 after:w-2 after:-translate-x-1/2 after:rounded-full after:bg-green',
            isSelected && 'bg-green-light',
          )}
        >
          {day}
        </button>
      </CalendarRangeHighlight>
    </td>
  );
}
